import { AxiosError } from 'axios';
import { types, flow, Instance } from 'mobx-state-tree';
import axios from 'utils/axios.config';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { IParserForm } from 'components/Forms/ParsersForm/types';
import { ErrorData, StatusTypeReq, IResponse } from 'types/common';
import { IParsersList, IParserData, IParserDataReq, SyncType } from './types';

const { model, frozen, maybeNull } = types;

export const ParsersStore = model('ParsersStore', {
  parsersList: maybeNull(frozen<IParsersList[]>()),
  parserData: maybeNull(frozen<IParserData>()),
})
  .extend(withAlertStore)
  .actions((self) => ({
    setParsersList(data: IParsersList[] | null) {
      self.parsersList = data;
    },
    setParserData(data: IParserData | null) {
      self.parserData = data;
    },
  }))
  .actions((self) => ({
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      const msg = response?.data.message || 'Internal server error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
  }))
  .actions((self) => ({
    getParsersList: flow(function* () {
      yield axios
        .get('crawlers')
        .then(({ data: { data } }) => self.setParsersList(data))
        .catch(self.errorHandling);
    }),
    changeParsersStatus: flow(function* ({ id, status }: StatusTypeReq) {
      yield axios
        .patch(`crawlers/status/${id}`, { status })
        .then(() => {
          if (self.parsersList) {
            self.setParsersList(self.parsersList.map((item) => (item.id === id ? { ...item, status } : item)));
          }
        })
        .catch(self.errorHandling);
      return id;
    }),
    resyncParsers: flow(function* (id: string) {
      yield axios
        .get(`crawlers/resync/${id}`)
        .then(({ data: { data } }) =>
          self.setParsersList(self.parsersList?.map((item) => (item.id === id ? data : item)) || [])
        )
        .catch(self.errorHandling);
      return id;
    }),
    getParserData: flow(function* (id: string) {
      yield axios
        .get(`crawlers/${id}`)
        .then(({ data: { data } }) => self.setParserData(data))
        .catch(self.errorHandling);
    }),
    addParser: flow(function* ({ brand, type, ...rest }: IParserForm) {
      if (!brand || !type) {
        self.alertStore.showAlert('Brand and Type are required fields', 'error');
        throw new Error('Brand and Type are required fields');
      }
      const newParser: IParserDataReq = {
        ...rest,
        brandId: brand.id,
        type: type.id as SyncType,
      };
      return yield axios
        .post(`crawlers`, newParser)
        .then(({ data: { data } }) => data)
        .catch(self.errorHandling);
    }),
    editParser: flow(function* ({ brand, type, id, ...rest }: IParserForm) {
      if (!brand || !type) {
        self.alertStore.showAlert('Brand and Type are required fields', 'error');
        throw new Error('Brand and Type are required fields');
      }
      const newParser: IParserDataReq = {
        ...rest,
        brandId: brand.id,
        type: type.id as SyncType,
      };
      yield axios.patch(`crawlers/${id}`, newParser).catch(self.errorHandling);
      return null;
    }),
  }));

export type IParsersStore = Instance<typeof ParsersStore>;
